import type {AABB} from "@cm/math"
import {Matrix4, Vector3, Quaternion} from "@cm/math"
import type {IMeshGeometryQueries} from "#template/interfaces/scene-manager"

export function calculateFov(filmGauge: number, focalLength: number, width: number, height: number): [number, number] {
    if (width > height) {
        return [2 * Math.atan(filmGauge / (2 * focalLength)), 2 * Math.atan((filmGauge * (height / width)) / (2 * focalLength))]
    } else {
        return [2 * Math.atan((filmGauge * (width / height)) / (2 * focalLength)), 2 * Math.atan(filmGauge / (2 * focalLength))]
    }
}

export function cameraLookAt(position: Vector3, target: Vector3): Matrix4 {
    let vz = position.sub(target).normalized()
    const up = new Vector3(0, 1, 0)
    let vx = up.cross(vz)
    if (vx.norm() === 0) {
        vz.z += 0.0001
        vz = vz.normalized()
        vx = up.cross(vz)
    }
    vx = vx.normalized()
    const vy = vz.cross(vx)
    return new Matrix4([vx.x, vx.y, vx.z, 0, vy.x, vy.y, vy.z, 0, vz.x, vz.y, vz.z, 0, position.x, position.y, position.z, 1])
}

export function cameraAutomaticTarget(
    position: Vector3,
    target: Vector3,
    aabb: AABB,
    focalLength: number,
    filmGauge: number,
    uiSize: [number, number],
): Matrix4 {
    const [fovX, fovY] = calculateFov(filmGauge, focalLength, ...uiSize)
    const [[minX, minY, minZ], [maxX, maxY, maxZ]] = aabb
    const sizeEps = 1e-3
    if (maxX - minX < sizeEps && maxY - minY < sizeEps && maxZ - minZ < sizeEps) {
        // use original positions if bounding box is zero size
    } else {
        const [cx, cy, cz] = target.toArray()
        const distTo = (x: number, y: number, z: number) => {
            const dx = x - cx
            const dy = y - cy
            const dz = z - cz
            return Math.sqrt(dx * dx + dy * dy + dz * dz)
        }
        const radiusXZ = Math.max(distTo(minX, cy, minZ), distTo(maxX, cy, minZ), distTo(minX, cy, maxZ), distTo(maxX, cy, maxZ))
        const radiusXYZ = Math.max(
            distTo(minX, minY, minZ),
            distTo(maxX, minY, minZ),
            distTo(minX, maxY, minZ),
            distTo(maxX, maxY, minZ),
            distTo(minX, minY, maxZ),
            distTo(maxX, minY, maxZ),
            distTo(minX, maxY, maxZ),
            distTo(maxX, maxY, maxZ),
        )
        const distance = Math.max(radiusXZ / Math.tan(fovX / 2), radiusXYZ / Math.tan(fovY / 2))

        if (target.y > position.y) {
            position = new Vector3(position.x, target.y, position.z)
        }
        position = position.sub(target).normalized().mul(distance).add(target)
    }
    return cameraLookAt(position, target)
}

export function automaticVerticalTilt(
    cameraTransformIn: Matrix4,
    filmGauge: number,
    focalLength: number,
    width: number,
    height: number,
): readonly [Matrix4, number] {
    const {position: positionCamera, quaternion: quaternionCamera, scale: scaleCamera} = cameraTransformIn.decompose()

    const normal = quaternionCamera.multiplyVector(new Vector3(0, 0, -1))
    const positionTarget = positionCamera.add(normal.mul(100))

    const targetAbove = positionTarget.y > positionCamera.y
    const positionNew = new Vector3(positionTarget.x, positionCamera.y, positionTarget.z)

    const directionTarget = positionTarget.sub(positionCamera).normalized()
    const directionNew = positionNew.sub(positionCamera).normalized()

    let rotationQuaternion = Quaternion.fromUnitVectors(directionTarget, directionNew)

    rotationQuaternion = rotationQuaternion.multiply(quaternionCamera)
    const cameraTransform = Matrix4.compose(positionCamera, rotationQuaternion, scaleCamera)

    let rotationAngle = quaternionCamera.angleTo(rotationQuaternion)

    if (targetAbove) {
        rotationAngle = -rotationAngle
    }

    const [_fovX, fovY] = calculateFov(filmGauge, focalLength, width, height)
    const additionalShiftY = -Math.tan(rotationAngle) / (2 * Math.tan(fovY / 2))

    return [cameraTransform, additionalShiftY]
}

export function targetFromTransform(value: Matrix4, distance: number): Vector3 {
    const {position, quaternion} = value.decompose()
    const targetDirection = quaternion.multiplyVector(new Vector3(0, 0, -1))
    return position.add(targetDirection.mul(distance))
}

export type ImagePlaneShift = {shiftX: number; shiftY: number; fovX: number; fovY: number}

export function cameraAimDirection(transform: Matrix4, shift?: ImagePlaneShift): Vector3 {
    const {quaternion} = transform.decompose()
    const forward = quaternion.multiplyVector(new Vector3(0, 0, -1))
    if (!shift) return forward

    const {shiftX, shiftY, fovX, fovY} = shift
    if ((shiftX === 0 && shiftY === 0) || !Number.isFinite(fovX) || !Number.isFinite(fovY)) return forward

    const right = quaternion.multiplyVector(new Vector3(1, 0, 0))
    const up = quaternion.multiplyVector(new Vector3(0, 1, 0))
    return forward
        .add(right.mul(2 * shiftX * Math.tan(fovX / 2)))
        .add(up.mul(2 * shiftY * Math.tan(fovY / 2)))
        .normalized()
}

export function raycastCameraDistance(
    meshGeometryQueries: IMeshGeometryQueries,
    cameraPosition: Vector3,
    aimDirection: Vector3,
    fallbackDistance: number,
): number {
    return meshGeometryQueries.raycastScene(cameraPosition, aimDirection) ?? fallbackDistance
}
